/*
 * Invites: an owner or manager invites someone by email (they pick a password) or by phone (crew, who sign in with texted codes).
 */
import { z } from "zod";
import { phoneSchema } from "./api.zod.js";
import { LOGO_TYPES } from "./companies.zod.js";
import { ROLES } from "./core.js";
import { TEAM_ROLES } from "./permissions.js";

export const inviteBody = z
  .object({
    role: z.enum(ROLES),
    firstName: z.string().trim().min(1).max(60),
    lastName: z.string().trim().max(60).default(""),
    email: z.email().trim().toLowerCase().optional(),
    phone: phoneSchema.optional(),
    locale: z.enum(["en", "es"]).default("en"),
    personId: z.uuid().optional(),
  })
  .refine((b) => Boolean(b.email) !== Boolean(b.phone), { message: "Give an email or a phone number, not both." });

export const inviteAnswer = z.object({
  id: z.uuid(),
  role: z.enum(ROLES),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  expiresAt: z.string(),
  personId: z.uuid(),
});

export const invitePeek = z.object({
  companyName: z.string(),
  role: z.enum(ROLES),
  firstName: z.string(),
  by: z.enum(["email", "phone"]),
});

export const inviteAcceptBody = z.object({
  password: z.string().min(10, "Use at least 10 characters.").max(200).optional(),
});

/** A person in the team list. Names show as first name and last initial. */
export const personRow = z.object({
  id: z.uuid(),
  name: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  hasLogin: z.boolean(),
  seesPrices: z.boolean(),
  /** The My pay switch: off hides My pay from them and its routes refuse them. */
  showsMyPay: z.boolean(),
  /** Counted on schedule capacity, and their time off takes a person away. By role until the office sets it. */
  countsOnSchedule: z.boolean(),
  active: z.boolean(),
  locale: z.string(),
  /** Their circle's color, or null for the one personColor picks from their id. */
  color: z.string().nullable(),
  /** Their profile picture's file id, or null. */
  photo: z.uuid().nullable(),
});

/**
 * Colors for a person's circle: Apple's system colors in their Increase Contrast shades, dark enough for white letters. Red
 * is left out, it means a problem.
 */
export const PERSON_COLORS = [
  "#0040DD",
  "#248A3D",
  "#C93400",
  "#8944AB",
  "#0071A4",
  "#D30F45",
  "#3634A3",
  "#0C817B",
  "#B25000",
  "#7F6545",
  "#008299",
] as const;
const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Pick a color.");

/** A person's color: their own pick, or the one from PERSON_COLORS their id always lands on. */
export function personColor(p: { id: string; color?: string | null }): string {
  if (p.color) return p.color;
  let n = 0;
  for (const ch of p.id) n = (n * 31 + ch.charCodeAt(0)) >>> 0;
  return PERSON_COLORS[n % PERSON_COLORS.length]!;
}

/** What the office can change on a person from the team list. */
export const personPatch = z
  .object({
    seesPrices: z.boolean(),
    showsMyPay: z.boolean(),
    countsOnSchedule: z.boolean(),
    active: z.boolean(),
    locale: z.enum(["en", "es"]),
    color: hexColor.nullable(),
  })
  .partial()
  .strict();

/** A new role for someone on the team (Team page). */
export const personRoleBody = z.object({ role: z.enum(TEAM_ROLES) }).strict();

/** Me on this company's team, with my circle's color and picture. */
export const myPerson = z.object({ id: z.uuid(), name: z.string(), color: z.string().nullable(), photo: z.uuid().nullable() });
export type MyPerson = z.infer<typeof myPerson>;
export const myPersonPatch = z.object({ color: hexColor.nullable() }).strict();
/** A profile picture. The web app shrinks it to a small square first. */
export const personPhotoUpload = z.object({
  name: z.string().trim().min(1).max(200),
  mime: z.enum(LOGO_TYPES, "Use a PNG, JPG or WebP picture."),
  dataBase64: z.string().max(1_400_000, "That picture is too big. The most is 1 MB."),
});
